import type { Auth, User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { ROLE_COLUMNS } from "./columns";
import { go, useCrumb, useTitle } from "./hooks";
import { useHoldReload } from "./offline/hold";
import { BUILT_IN_ONLY, changedFrom, customLetters, letterChoices, letterText, nameProblem, resourceText, roleRows, sameAccessText } from "./roles";
import { type Saved, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem, Status } from "./UserPages";
import { roleName } from "./users";
import { Skeleton } from "./Skeleton";

const open = (path: string, notice?: string) => {
  history.pushState(notice ? { notice } : null, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
};

/** Design 12.5, WF-12: the built-in roles and the custom roles. Only an administrator sees this page (rule U-12). */
export function RoleList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<api.RoleList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [making, setMaking] = useState(false);
  const [notice] = useState<string | null>(() => {
    const found: unknown = history.state?.notice;
    return typeof found === "string" ? found : null;
  });
  const [sort, setSort] = useSort("sort", ROLE_COLUMNS);
  useTitle(`Roles · ${org.name}`);
  useEffect(() => {
    if (notice) history.replaceState(null, "", location.href);
  }, [notice]);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.roles)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="roles-hidden">
        <h1 tabIndex={-1}>Roles</h1>
        <p>Only an administrator of the whole organization sees the roles.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  return (
    <main className="roles-page wide" data-testid="roles-page">
      <h1 tabIndex={-1}>Roles</h1>
      <p className="sub">A custom role starts from a built-in role and can give less access, never more. Give it to a member on the Users page.</p>
      {notice && (
        <p role="status" data-testid="roles-notice">
          {notice}
        </p>
      )}
      {list && !making && (
        <div className="tabs">
          <button type="button" className="primary" data-testid="role-make" onClick={() => setMaking(true)}>
            Make a custom role
          </button>
        </div>
      )}
      {list && making && <MakeForm base={base} list={list} auth={auth} user={user} onCancel={() => setMaking(false)} />}
      {found === undefined ? (
        <Skeleton shape="table" columns={3} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <SortSelect id="roles-select" columns={ROLE_COLUMNS} sort={sort} onSort={setSort} fallback="Built-in roles first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Roles">
            <table className="list" data-testid="role-table" data-cols="roles-select">
              <thead>
                <tr>
                  {ROLE_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(roleRows(found, (role) => roleName(role)), ROLE_COLUMNS, sort).map((row) => (
                  <tr key={row.key} data-testid="role-row" data-kind={row.kind} data-name={row.name}>
                    <th scope="row">
                      {row.id ? (
                        <a href={`${base}/roles/${row.id}`} onClick={go} data-testid="role-link">
                          {row.name}
                        </a>
                      ) : (
                        row.name
                      )}
                    </th>
                    <td data-label="Kind">{row.kind === "custom" ? "Custom" : "Built-in"}</td>
                    <td data-label="Based on">{row.base || "—"}</td>
                    <td className="num" data-label="Modules">{row.modules}</td>
                    <td className="num" data-label="Members">{row.assigned ?? "—"}</td>
                    <td data-label="Added">{row.created_at ? dayText(row.created_at.slice(0, 10), true) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function MakeForm({ base, list, auth, user, onCancel }: { base: string; list: api.RoleList; auth: Auth; user: User; onCancel: () => void }) {
  const [name, setName] = useState("");
  const [basedOn, setBasedOn] = useState(list.built_in.find((role) => role.key === "viewer")?.key ?? list.built_in[0]?.key ?? "");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const taken = [...list.custom.map((role) => role.name), ...list.built_in.flatMap((role) => [role.key, roleName(role.key)])];
  useHoldReload(name.trim() !== "", "the new role");
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = nameProblem(name, taken);
    if (problem) return setSaved({ ok: false, text: problem });
    const letters = customLetters(list.built_in.find((role) => role.key === basedOn)?.letters ?? {});
    setSaving(true);
    setSaved(null);
    try {
      const made = await api.createRole(await user.getIdToken(), { name: name.trim(), based_on: basedOn, letters });
      open(`${base}/roles/${made.id}`);
    } catch (e) {
      setSaved(saveFailure(e, "The role"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="role-form" aria-labelledby="role-form-title">
      <h2 id="role-form-title">Make a custom role</h2>
      <p className="sub">The new role starts with the access of the built-in role. Then you can remove access, module by module.</p>
      <label htmlFor="role-name">Name</label>
      <input id="role-name" data-testid="role-name" value={name} maxLength={60} autoComplete="off" onChange={(e) => setName(e.target.value)} />
      <label htmlFor="role-base">Copy from</label>
      <select id="role-base" data-testid="role-base" value={basedOn} onChange={(e) => setBasedOn(e.target.value)}>
        {list.built_in.map((role) => (
          <option key={role.key} value={role.key}>
            {roleName(role.key)}
          </option>
        ))}
      </select>
      <div className="actions">
        <button type="submit" className="primary" data-testid="role-create" disabled={saving}>
          Make the role
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="role-form-status" />
    </form>
  );
}

/** One custom role: the access of each module, up to the access of its base role (rule U-12). */
export function RolePage({ id, base, org, auth, user }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User }) {
  const [found, setFound] = useState<api.RoleDetail | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [letters, setLetters] = useState<api.Letters>({});
  const [name, setName] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [asking, setAsking] = useState(false);
  const questionRef = useRef<HTMLParagraphElement>(null);
  const deleteRef = useRef<HTMLButtonElement>(null);
  const detail = typeof found === "object" ? found : null;
  const dirty = detail !== null && (name.trim() !== detail.name || detail.resources.some((resource) => (letters[resource] ?? "") !== (detail.letters[resource] ?? "")));
  useHoldReload(dirty, "the role");
  useTitle(`${detail ? detail.name : "Role"} · ${org.name}`);
  useCrumb(detail ? detail.name : null);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.role(token, id))
      .then((next) => {
        if (!live) return;
        setFound(next);
        setLetters(next.letters);
        setName(next.name);
      })
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id, attempt]);
  useEffect(() => {
    if (asking) questionRef.current?.focus();
  }, [asking]);
  if (found === "hidden") {
    return (
      <main data-testid="role-missing">
        <h1 tabIndex={-1}>Role not found</h1>
        <p>
          No custom role at this link is visible to you.{" "}
          <a href={`${base}/roles`} onClick={go}>
            Go to the roles
          </a>
        </p>
      </main>
    );
  }
  if (!detail) {
    return <main>{typeof found === "string" ? <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} /> : <Skeleton shape="detail" />}</main>;
  }
  const changed = changedFrom(letters, detail.base_letters, detail.resources);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const problem = nameProblem(name, []);
    if (problem) return setSaved({ ok: false, text: problem });
    setSaving(true);
    setSaved(null);
    try {
      const next = await api.changeRole(await user.getIdToken(), id, { name: name.trim(), letters: customLetters(letters) });
      setFound({ ...detail, ...next });
      setLetters(next.letters);
      setSaved({ ok: true, text: "Saved. The members with this role have the new access now." });
    } catch (e) {
      setSaved(saveFailure(e, "The role"));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    setSaving(true);
    setSaved(null);
    try {
      await api.deleteRole(await user.getIdToken(), id);
      open(`${base}/roles`, `Deleted the role "${detail.name}".`);
    } catch (e) {
      setSaved(saveFailure(e, "The deletion"));
      setSaving(false);
    }
  };
  return (
    <main className="role-page wide" data-testid="role-page">
      <h1 tabIndex={-1}>{detail.name}</h1>
      <p className="sub">
        Based on {roleName(detail.based_on)}. {detail.assigned === 1 ? "1 member has" : `${detail.assigned} members have`} this role.{" "}
        <a href={`${base}/users`} onClick={go}>
          Go to the users
        </a>
      </p>
      <form onSubmit={save} noValidate data-testid="role-edit">
        <label htmlFor="role-rename">Name</label>
        <input id="role-rename" data-testid="role-rename" value={name} maxLength={60} autoComplete="off" onChange={(e) => setName(e.target.value)} />
        <p className="sub" data-testid="role-changed">
          {changed.length ? `Less than ${roleName(detail.based_on)} in ${changed.length} ${changed.length === 1 ? "module" : "modules"}.` : sameAccessText(detail.based_on, roleName(detail.based_on))}
        </p>
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Access of each module">
          <table className="list role-matrix" data-testid="role-matrix">
            <thead>
              <tr>
                <th scope="col">Module</th>
                <th scope="col">Access</th>
                <th scope="col">{roleName(detail.based_on)}</th>
              </tr>
            </thead>
            <tbody>
              {detail.resources.map((resource) => {
                const choices = letterChoices(detail.base_letters[resource]);
                const value = letters[resource] ?? "";
                if (BUILT_IN_ONLY.includes(resource)) {
                  return (
                    <tr key={resource} data-testid="role-module" data-module={resource}>
                      <th scope="row">{resourceText(resource)}</th>
                      <td data-label="Access" className="sub" data-testid="role-letter-locked">
                        Built-in administrator only
                      </td>
                      <td data-label={roleName(detail.based_on)}>{letterText(detail.base_letters[resource])}</td>
                    </tr>
                  );
                }
                return (
                  <tr key={resource} data-testid="role-module" data-module={resource} className={changed.includes(resource) ? "changed" : undefined}>
                    <th scope="row">
                      <label htmlFor={`letter-${resource}`}>{resourceText(resource)}</label>
                    </th>
                    <td data-label="Access">
                      <select
                        id={`letter-${resource}`}
                        data-testid="role-letter"
                        value={value}
                        disabled={choices.length < 2}
                        onChange={(e) => setLetters({ ...letters, [resource]: e.target.value })}
                      >
                        {choices.map((letter) => (
                          <option key={letter} value={letter}>
                            {letterText(letter)}
                          </option>
                        ))}
                      </select>
                      {changed.includes(resource) && <span className="chip">Changed</span>}
                    </td>
                    <td data-label={roleName(detail.based_on)}>{letterText(detail.base_letters[resource])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="actions">
          <button type="submit" className="primary" data-testid="role-save" disabled={saving || !dirty}>
            Save
          </button>
          <button type="button" ref={deleteRef} data-testid="role-delete" disabled={saving || detail.assigned > 0 || asking} onClick={() => setAsking(true)}>
            Delete the role
          </button>
        </div>
        {asking && (
          <div className="confirm" data-testid="role-delete-ask">
            <p ref={questionRef} tabIndex={-1}>
              Delete the role "{detail.name}"? You cannot undo this.
            </p>
            <div className="actions">
              <button type="button" className="danger" data-testid="role-delete-confirm" disabled={saving} onClick={() => void remove()}>
                Delete the role
              </button>
              <button
                type="button"
                onClick={() => {
                  setAsking(false);
                  requestAnimationFrame(() => deleteRef.current?.focus());
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        {detail.assigned > 0 && <p className="sub">To delete the role, give its members a different role first.</p>}
        <Status saved={saved} auth={auth} testId="role-status" />
      </form>
    </main>
  );
}
